import { useEffect, useRef, useState } from "react";
import { SectionHeading, BetaBadge } from "../common/ui";
import { SCENES } from "./FrothAiScenes";

// "How it works" on /frothai: four tabs, each with numbered steps beside an animated
// walkthrough of that flow in the product (FrothAiScenes.js). One clock drives both,
// so each step lights up as the walkthrough reaches it. When a walkthrough ends the
// next tab opens by itself, unless the visitor is hovering or using the keyboard in
// the section, in which case the walkthrough replays. The clock only runs while the
// section is on screen; reduced-motion visitors get each walkthrough's finished state.

const guides = [
  {
    id: "requirements",
    label: "Generate from Requirements",
    alt: "Walkthrough: picking generation criteria for a requirement, FrothAI generating in the background, then reviewing and inserting the scenarios and test cases",
    title: "Generate test scenarios and test cases from requirements",
    beta: true,
    steps: [
      "Select one or more requirements and choose your generation criteria: functional or non-functional, smoke, sanity, regression or UAT, and coverage types such as positive, negative, edge, exception, UI/UX and data-driven.",
      "Add any testing expectations. FrothAI generates in the background and shows live progress, so you can keep working.",
      "Review the suggested scenarios and test cases interactively, refine them, and insert only the ones you want.",
    ],
  },
  {
    id: "autofill",
    label: "Auto-fill a Test Case",
    alt: "Walkthrough: typing a test case name, clicking the FrothAI icon, and FrothAI filling the summary, steps, expected results, priority and pre-condition",
    title: "Auto-fill a test case with FrothAI",
    steps: [
      "Open Create Test Case in the Test Management module, enter the essential details, then click the FrothAI icon next to the test case name.",
      "FrothAI analyses the context and fills the summary, detail steps, expected results and other key fields. Review, refine and submit.",
    ],
  },
  {
    id: "scripts",
    label: "Automation Scripts",
    alt: "Walkthrough: choosing Web and Generate with FrothAI, picking an approved test case, FrothAI writing a Playwright script, running it live and changing it by chat",
    title: "Generate automation scripts with FrothAI",
    beta: true,
    steps: [
      "When creating an automation script, choose the application type and select 'Generate with FrothAI'.",
      "Pick approved test cases. FrothAI reads the steps, maps them to selectors and writes a Playwright (web) or Appium (Android and iOS) script.",
      "Run the script live to watch each step, or ask FrothAI to make a change for you.",
    ],
  },
  {
    id: "ask",
    label: "Ask FrothAI",
    alt: "Walkthrough: opening Ask FrothAI on the project dashboard, asking which requirements have no test cases, and getting an answer with links to the records",
    title: "Ask FrothAI about your project",
    beta: true,
    steps: [
      "Open Ask FrothAI from any page. It works on the project you have selected.",
      "Ask questions like 'Which requirements have no test cases?' or 'What are the coverage gaps?'.",
      "Get answers with evidence and links straight to the records. Ask FrothAI reads your data; it doesn't change it.",
    ],
  },
];

const PAINT_MS = 32; // ~30 fps is plenty for these walkthroughs

export default function FrothAiGuides() {
  const [active, setActive] = useState(0);
  // 1 when moving forward, -1 when moving back; the new guide slides in from that side.
  const [dir, setDir] = useState(1);
  const [t, setT] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const [inView, setInView] = useState(false);
  const [reduced] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  const howRef = useRef(null);
  const tabsRef = useRef(null);
  const tRef = useRef(0);
  const autoRef = useRef(true);

  const guide = guides[active];
  const { ms, stepAt, Scene } = SCENES[guide.id];
  const auto = !hovered && !keyboardFocus && !reduced;
  autoRef.current = auto;
  const shownT = reduced ? ms : t;

  const select = i => {
    setDir(i >= active ? 1 : -1);
    tRef.current = 0;
    setT(0);
    setActive(i);
  };

  // The clock: runs while on screen, pauses (keeping its place) when scrolled away.
  useEffect(() => {
    if (!inView || reduced) return undefined;
    let raf;
    let prev = performance.now();
    let painted = 0;
    const tick = now => {
      let acc = tRef.current + Math.min(now - prev, 100);
      prev = now;
      if (acc >= ms) {
        if (autoRef.current) {
          tRef.current = 0;
          setT(0);
          setDir(1);
          setActive(a => (a + 1) % guides.length);
          return;
        }
        acc = 0; // held by hover or keyboard: replay this walkthrough
      }
      tRef.current = acc;
      if (now - painted >= PAINT_MS) {
        painted = now;
        setT(acc);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduced, active, ms]);

  useEffect(() => {
    const el = howRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.35 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Where the tabs scroll sideways (phones), glide the selected one into the middle of the strip.
  // Only the strip scrolls, never the page.
  useEffect(() => {
    const strip = tabsRef.current;
    const tab = strip?.children[active];
    if (!tab || strip.scrollWidth <= strip.clientWidth) return;
    strip.scrollTo({
      left: Math.max(0, tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2),
      behavior: reduced ? "auto" : "smooth",
    });
  }, [active, reduced]);

  const current = stepAt.reduce((last, at, k) => (shownT >= at ? k : last), 0);

  return (
    <section
      ref={howRef}
      className={`ft-section ft-section--alt fai-screen fai-how${auto ? " is-auto" : ""}`}
      style={{ "--dir": dir }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Only keyboard focus holds it; a mouse click on a tab lets it carry on once the pointer leaves.
      onFocus={e => setKeyboardFocus(e.target.matches?.(":focus-visible") ?? false)}
      onBlur={e => !e.currentTarget.contains(e.relatedTarget) && setKeyboardFocus(false)}
    >
      <div className="ft-container">
        <SectionHeading
          eyebrow="How it works"
          title={
            <>
              See FrothAI <span>in action</span>
            </>
          }
        />
        <div className="ft-tabs justify-content-center" role="tablist" aria-label="FrothAI guides" ref={tabsRef}>
          {guides.map((g, i) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              id={`tab-${g.id}`}
              aria-selected={active === i}
              aria-controls={`panel-${g.id}`}
              className="ft-tab"
              style={active === i ? { "--p": shownT / ms } : undefined}
              onClick={() => select(i)}
            >
              {g.label}
            </button>
          ))}
        </div>
        <div className="pg-panel ft-split" key={guide.id} role="tabpanel" id={`panel-${guide.id}`} aria-labelledby={`tab-${guide.id}`}>
          <div>
            <h3 className="ft-heading pg-guide-title">
              {guide.title} {guide.beta && <BetaBadge />}
            </h3>
            <ol className="pg-guide-steps">
              {guide.steps.map((s, k) => (
                <li
                  key={s}
                  style={{ "--k": k }}
                  className={`${shownT >= stepAt[k] ? "is-on" : ""}${k === current ? " is-current" : ""}`}
                >
                  {s}
                </li>
              ))}
            </ol>
          </div>
          <figure className="ft-frame faw-frame m-0" role="img" aria-label={guide.alt}>
            <div className="ft-frame__bar" aria-hidden="true">
              <span />
              <span />
              <span />
              <div className="ft-frame__url">app.frothtestops.com</div>
            </div>
            <div aria-hidden="true">
              <Scene t={shownT} />
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}
